import api from '@api';
import AudienceSelector from '@components/layout/main/AudienceSelector';
import DatasetSelector from '@components/layout/main/DatasetSelector';
import PromptMenu from '@components/layout/main/PromptMenu';
import config from '@config';
import { css, Global } from '@emotion/react';
import { Log, MessageType } from '@enums';
import { useTouchDevice, useWindowSize } from '@hooks';
import AudienceIcon from '@icons/audience.svg?react';
import DeleteIcon from '@icons/close.svg?react';
import CollapseIcon from '@icons/collapse.svg?react';
import FolderIcon from '@icons/folder.svg?react';
import SendIcon from '@icons/send.svg?react';
import StopIcon from '@icons/stop.svg?react';
import { DatasetPicker, type DatasetTreeChange } from '@logic/datasetPicker';
import { type ChatStore, useChatStore } from '@store/chat';
import { useProfileStore } from '@store/profile';
import { useShellStore } from '@store/shell';
import { PrimaryButton as Button } from '@ui/buttons';
import { Pill } from '@ui/pills';
import { Tooltip } from '@ui/tooltip';
import * as utils from '@utils';
import type {
  InputEvent,
  KeyboardEvent,
  MouseEvent,
  Ref,
  TouchEvent
} from 'react';
import {
  Fragment,
  forwardRef,
  memo,
  startTransition,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import type { Dataset, DatasetTree, RelevantAudience } from 'types/schema';
import Styled from './PromptInput.styled';

interface Props {
  autoFocus?: boolean;
  className?: string;
  datasets: Dataset[];
  disabled?: boolean;
  chat: ChatStore['chat'];
  placeholder?: string;
  isLoading?: boolean;
  selectedDataset: Dataset | null;
  onAudienceChange?: (
    action: 'add' | 'remove',
    audience?: RelevantAudience,
    selectedAudiences?: RelevantAudience[]
  ) => void;
  onCancel: () => void;
  onConfirmDatasetChange?: (dataset: Dataset) => void;
  onSubmit: (
    message: string,
    origin: 'click' | 'enter',
    audiences: RelevantAudience[]
  ) => void;
}

function PromptInput(props: Props, ref: Ref<HTMLTextAreaElement>) {
  const chatId = useChatStore((state) => state.chat.id);
  const pushMessage = useChatStore((state) => state.pushMessage);
  const hasStartedChat = useChatStore((state) => state.hasStartedChat);
  const setToast = useShellStore((state) => state.setToast);
  const selectedAudiences = useChatStore((state) => state.selectedAudiences);
  const selectedDataset = useChatStore((state) => state.selectedDataset);
  const setSelectedAudiences = useChatStore(
    (state) => state.setSelectedAudiences
  );
  const setSelectedDatasetIds = useChatStore(
    (state) => state.setSelectedDatasetIds
  );
  const setIsSelectedAudienceTooltipOpen = useShellStore(
    (state) => state.setIsSelectedAudienceTooltipOpen
  );
  const isSelectedAudienceTooltipOpen = useShellStore(
    (state) => state.isSelectedAudienceTooltipOpen
  );
  const { feat } = useProfileStore();
  const [inputValue, setInputValue] = useState('');
  const cachedDatasetTreeRef = useRef<DatasetTree | null>(
    utils.getStorageItem<DatasetTree>('octopus.dataset_tree', sessionStorage)
  );
  const [datasetTree, setDatasetTree] = useState<DatasetTree | null>(
    cachedDatasetTreeRef.current
  );
  const [datasetTreeChange, setDatasetTreeChange] =
    useState<DatasetTreeChange | null>(null);
  const [isSelectedAudienceMenuOpen, setIsSelectedAudienceMenuOpen] =
    useState(false);
  const windowSize = useWindowSize();
  const isTouchDevice = useTouchDevice();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const selectedAudiencesMenuRef = useRef<HTMLMenuElement>(null);
  const controlsBarRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const textAreaLineHeightRef = useRef<number>(0);
  const { selected, maxSize } = config.prompt.audiences;
  const maxSelectedAudiencesReached =
    selectedAudiences.length === selected.maxSize;
  const maxVisibleAudiencesExceeded = selectedAudiences.length > maxSize;
  const shouldShowBadge = maxVisibleAudiencesExceeded || windowSize.isSmall;

  useImperativeHandle(ref, () => textAreaRef.current);

  useEffect(() => {
    if (cachedDatasetTreeRef.current) {
      cachedDatasetTreeRef.current = null;
      return;
    }

    api.chats.getDatasetTree(chatId).then((datasetTree) => {
      // Cache dataset tree in sessionStorage
      window.sessionStorage.setItem(
        'octopus.dataset_tree',
        JSON.stringify(datasetTree)
      );

      setDatasetTree(datasetTree);
    });
  }, [chatId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    const { current: badgeElement } = badgeRef;
    const { current: menuElement } = selectedAudiencesMenuRef;

    // Skip if refs aren't available yet
    if (!(badgeElement && menuElement)) {
      return;
    }

    const badgeRect = badgeElement.getBoundingClientRect();

    // Check if menu would overflow viewport when placed below
    if (badgeRect.bottom + config.prompt.menu.maxHeight > window.innerHeight) {
      menuElement.style.setProperty('bottom', 'calc(100% + 8px)');
    } else {
      menuElement.style.setProperty('top', 'calc(100% + 8px)');
    }
  }, [selectedAudiences, props.disabled]);

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaLineHeightRef.current = textAreaRef.current.clientHeight;
    }
  }, []);

  const handleDeselectAudience = useCallback(
    (audience: RelevantAudience) => {
      const remainingAudiences = selectedAudiences.filter(
        (item) => item.id !== audience.id
      );

      if (remainingAudiences.length === 0) {
        setIsSelectedAudienceMenuOpen(false);
      }

      setSelectedAudiences(remainingAudiences);

      api.analytics.deselectedAudience({
        ['audience_id']: audience.id,
        ['audience_name']: audience.title,
        ['audience_type']: audience.type,
        ['chat_id']: props.chat.id,
        ['dataset_details']: selectedDataset.title
      });

      props.onAudienceChange?.('remove', audience, remainingAudiences);
    },
    [
      props.chat.id,
      props.onAudienceChange,
      selectedAudiences,
      selectedDataset,
      setSelectedAudiences
    ]
  );

  const handleInputKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Enter' && !event.shiftKey && inputValue.trim()) {
      handleSubmit(inputValue, 'enter');

      // Skip line break when 'Enter' is pressed
      event.preventDefault();
    }
  };

  const handleSendButtonClick = () => {
    if (inputValue.trim()) {
      handleSubmit(inputValue, 'click');
    }
  };

  const handleSubmit = (value: string, origin: 'click' | 'enter') => {
    // Lose focus from input
    textAreaRef.current.blur();

    // Send message to server
    props.onSubmit(value, origin, selectedAudiences);

    // Clear input field
    setInputValue('');
  };

  const handleInputChange = (event: InputEvent<HTMLTextAreaElement>) => {
    if (!props.isLoading) {
      setInputValue(event.currentTarget.value);
    }

    // Auto adjusts the height of the textarea to fit the content
    utils.autoAdjustTextAreaHeight(
      event.currentTarget,
      textAreaLineHeightRef.current
    );
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (event.currentTarget === event.target) {
      textAreaRef.current?.focus();
    }
  };

  const handleBadgeTouchStart = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      if (event.currentTarget === event.target) {
        setIsSelectedAudienceMenuOpen(
          (isSelectedAudienceMenuOpen) => !isSelectedAudienceMenuOpen
        );
      }
    },
    []
  );

  const handleSelectAudience = useCallback(
    (audience: RelevantAudience) => {
      const augmentedAudiences = [...selectedAudiences, audience];

      setSelectedAudiences(augmentedAudiences);

      // Clear cached suggested audiences to force re-fetch
      window.sessionStorage.removeItem('octopus.suggested_audiences');

      api.analytics.selectedAudience({
        ['audience_id']: audience.id,
        ['audience_name']: audience.title,
        ['audience_type']: audience.type,
        ['chat_id']: props.chat.id,
        ['dataset_details']: selectedDataset.title
      });

      props.onAudienceChange?.('add', audience, augmentedAudiences);
    },
    [
      selectedAudiences,
      selectedDataset,
      props.chat.id,
      props.onAudienceChange,
      setSelectedAudiences
    ]
  );

  const handleSearchAudience = useCallback(() => {
    api.analytics.searchedAudience({
      ['chat_id']: props.chat.id,
      ['dataset_details']: selectedDataset.title
    });
  }, [props.chat.id, selectedDataset]);

  const handleOpenedAudienceMenu = useCallback(() => {
    api.analytics.openedAudienceMenu({
      ['chat_id']: props.chat.id,
      ['dataset_details']: selectedDataset.title
    });
  }, [props.chat.id, selectedDataset]);

  const handleDatasetTreeChange = useCallback(
    (datasetTree: DatasetTree, change: DatasetTreeChange) => {
      setDatasetTree(datasetTree);
      setDatasetTreeChange(change);
      setSelectedDatasetIds(change.selectedIds);
      utils.log('Current dataset tree changed', {
        data: change,
        type: Log.Debug
      });
    },
    [setSelectedDatasetIds]
  );

  const handleMenuClose = useCallback(() => {
    if (!datasetTreeChange || !hasStartedChat()) {
      return;
    }

    const toastTimeout = 2000;

    setToast({
      message: 'Success! New data set configuration',
      status: 'success',
      timeout: toastTimeout
    });

    let message = 'Updated primary data set configuration';

    if (datasetTreeChange?.action === 'activateEcosystem') {
      const { dataset } = datasetTreeChange;
      message = `Switched primary data set to "${dataset.title}"`;
    }

    window.setTimeout(() => {
      startTransition(() => {
        utils.log('Pushing system message to chat area.', Log.Notice);
        pushMessage({
          ['message_type']: MessageType.System,
          ['system_text']: message
        });
      });
    }, toastTimeout + 500);

    // Reset dataset tree change
    setDatasetTreeChange(null);
  }, [datasetTreeChange, hasStartedChat, pushMessage, setToast]);

  const shouldShowAudienceTitleTooltip = useCallback(
    (audienceTitle: RelevantAudience['title']) =>
      audienceTitle.length > config.prompt.audiences.title.maxLength,
    []
  );

  const selectedAudienceMenu = useMemo(
    () => (
      <Styled.Menu
        ref={selectedAudiencesMenuRef}
        hidden={
          !isSelectedAudienceMenuOpen && isTouchDevice && windowSize.isSmall
        }>
        <Styled.MenuLabel>
          Added Audiences ({selectedAudiences.length})
        </Styled.MenuLabel>
        <Styled.List className="static-scrollbar" scrollable>
          {selectedAudiences.map(
            (audience: RelevantAudience, index: number) => {
              const metadata = utils.getAudienceMetadata(audience.type);

              return (
                <Styled.ListItem
                  key={`selected:${audience.id}${index}`}
                  onClick={() => handleDeselectAudience(audience)}>
                  <Styled.ListItemIcon>{metadata.icon}</Styled.ListItemIcon>
                  <Styled.ListItemContent>
                    <Styled.ListItemTitle>
                      {shouldShowAudienceTitleTooltip(audience.title) ? (
                        <Tooltip
                          classesPopper="audience-tooltip-popper"
                          content={audience.title}
                          enterDelay={300}
                          leaveDelay={0}
                          placement="bottom">
                          <span>{audience.title}</span>
                        </Tooltip>
                      ) : (
                        audience.title
                      )}
                    </Styled.ListItemTitle>
                    <Styled.ListItemSubtitle>
                      {metadata.description}
                    </Styled.ListItemSubtitle>
                  </Styled.ListItemContent>
                  <Styled.ListItemIcon>
                    <DeleteIcon className="action-icon" />
                  </Styled.ListItemIcon>
                </Styled.ListItem>
              );
            }
          )}
        </Styled.List>
      </Styled.Menu>
    ),
    [
      isSelectedAudienceMenuOpen,
      isTouchDevice,
      windowSize,
      selectedAudiences,
      handleDeselectAudience,
      shouldShowAudienceTitleTooltip
    ]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  const selectedItems = useMemo(() => {
    if (selectedAudiences.length === 0) {
      return null;
    }

    const shouldShowAudienceHint =
      utils.getStorageItem<boolean>('octopus.dataset.show_audience_hint') ??
      isSelectedAudienceTooltipOpen;

    const selectedAudienceItems =
      shouldShowBadge && selectedAudiences.length > 0 ? (
        <Styled.Badge
          data-testid="chat-audience-badge"
          ref={badgeRef}
          disabled={props.isLoading}
          tabIndex={0}
          onBlur={() => setIsSelectedAudienceMenuOpen(false)}
          onTouchStart={handleBadgeTouchStart}>
          <AudienceIcon />
          <span>
            {windowSize.isSmall
              ? selectedAudiences.length
              : `${config.prompt.audiences.maxSize}+`}
          </span>
          {windowSize.isSmall && <CollapseIcon />}
          {selectedAudienceMenu}
        </Styled.Badge>
      ) : (
        // biome-ignore lint/complexity/noUselessFragments: OK
        <Fragment>
          {selectedAudiences.map((audience, index) => (
            <Pill
              key={`pill:${audience.id}${index}`}
              color="black"
              disabled={props.isLoading || props.disabled}
              label={audience.title}
              rootClassName={utils.getClassName({
                ['pill-root']: true,
                ['disabled']: props.isLoading || props.disabled
              })}
              endIcon={
                <Styled.IconWrapper
                  onClick={() => handleDeselectAudience(audience)}>
                  <DeleteIcon />
                </Styled.IconWrapper>
              }
              endIconClassName="pill-end-icon"
              iconsAndTextClassName="pill-icons-and-text"
              triggerClassName="pill-trigger"
              labelGridClassName="pill-label"
              tabIndex={0}
            />
          ))}
        </Fragment>
      );

    const handleButtonClick = () => {
      setIsSelectedAudienceTooltipOpen(false);
      window.localStorage.setItem(
        'octopus.dataset.show_audience_hint',
        'false'
      );
    };

    return (
      <Styled.SelectedItems overflow={shouldShowBadge ? 'visible' : 'hidden'}>
        <Tooltip
          arrow
          className="pill-tooltip"
          classesPopper="pill-tooltip-popper"
          classesArrow="pill-tooltip-arrow"
          open={shouldShowAudienceHint}
          placement="bottom-start"
          variant="navy"
          content={
            <Styled.PillTooltipWrapper>
              <Styled.PillTooltipHeader>
                Audience options depend on your current data set
              </Styled.PillTooltipHeader>
              <Styled.PillTooltipContent>
                To view audiences enabled by other data sets change the data set
                with the selector next to the “send” button.
              </Styled.PillTooltipContent>
              <Button onClick={handleButtonClick}>Got it</Button>
            </Styled.PillTooltipWrapper>
          }
          leaveDelay={200}>
          {selectedAudienceItems}
        </Tooltip>
      </Styled.SelectedItems>
    );
  }, [
    props.isLoading,
    props.disabled,
    isSelectedAudienceTooltipOpen,
    selectedAudiences,
    selectedAudienceMenu,
    shouldShowBadge,
    windowSize,
    handleDeselectAudience,
    handleBadgeTouchStart
  ]);

  const actionButton = props.isLoading ? (
    <Styled.StopButton
      tabIndex={0}
      onClick={props.onCancel}
      data-testid="stop-button">
      <Tooltip
        className="tooltip-wrapper"
        content="Stop"
        enterDelay={200}
        leaveDelay={0}
        placement="bottom-start">
        <StopIcon />
      </Tooltip>
    </Styled.StopButton>
  ) : (
    <Styled.SendButton
      data-testid="send-button"
      tabIndex={0}
      onClick={handleSendButtonClick}>
      <Tooltip
        className="tooltip-wrapper"
        content="Send message"
        enterDelay={200}
        leaveDelay={0}
        placement="bottom-start">
        <SendIcon />
      </Tooltip>
    </Styled.SendButton>
  );

  const input = props.isLoading ? (
    <Styled.Loading data-testid="loader-input" className="fade-in-out">
      Generating
    </Styled.Loading>
  ) : (
    <Styled.TextArea
      autoFocus={props.autoFocus}
      data-testid="text-area"
      disabled={props.disabled}
      enterKeyHint="send"
      id="prompt-input"
      placeholder={props.placeholder}
      ref={textAreaRef}
      rows={1}
      value={inputValue}
      onInput={handleInputChange}
      onKeyDown={handleInputKeyDown}
    />
  );

  const menuItems = useMemo(
    () => [
      {
        disabled: maxSelectedAudiencesReached,
        icon: <AudienceIcon />,
        label: 'Add audiences',
        tooltip: maxSelectedAudiencesReached ? (
          <>
            You added the maximum number of audiences.
            <br />
            Remove one to add audiences again.
          </>
        ) : undefined,
        widget: ({ close }) => (
          <AudienceSelector
            embedded
            selectedAudiences={selectedAudiences}
            selectedDataset={props.selectedDataset}
            onClose={close}
            onOpenedAudienceMenu={handleOpenedAudienceMenu}
            onSelectAudience={handleSelectAudience}
            onSearchAudience={handleSearchAudience}
          />
        )
      },
      {
        label: 'Configure data sets',
        icon: <FolderIcon />,
        widget: () => (
          <DatasetPicker
            className="dataset-picker"
            searchPlaceholder="Search data sets"
            tree={datasetTree}
            onTreeChange={handleDatasetTreeChange}
          />
        )
      }
    ],
    [
      selectedAudiences,
      props.selectedDataset,
      datasetTree,
      maxSelectedAudiencesReached,
      handleDatasetTreeChange,
      handleSearchAudience,
      handleOpenedAudienceMenu,
      handleSelectAudience
    ]
  );

  const controls = feat.datasetPicker.enabled ? (
    <PromptMenu
      disabled={props.disabled}
      items={menuItems}
      onClose={handleMenuClose}
    />
  ) : (
    <>
      <AudienceSelector
        selectedAudiences={selectedAudiences}
        selectedDataset={props.selectedDataset}
        onOpenedAudienceMenu={handleOpenedAudienceMenu}
        onSelectAudience={handleSelectAudience}
        onSearchAudience={handleSearchAudience}
      />
      {props.datasets.length > 1 && (
        <DatasetSelector
          datasets={props.datasets}
          disabled={props.disabled}
          selectedDataset={props.selectedDataset}
          onConfirmDatasetChange={props.onConfirmDatasetChange}
        />
      )}
    </>
  );

  return (
    <Fragment>
      <Global styles={styles} />
      <Styled.Wrapper
        className={props.className}
        disabled={props.disabled}
        onClick={handleClick}>
        {selectedItems}
        <Styled.TextAreaWrapper>
          {input}
          {/* Send/cancel button shown here if audience selection is enabled */}
          {!feat.audienceSelector.enabled && actionButton}
        </Styled.TextAreaWrapper>
        <Styled.ControlsBar
          className="controls-bar"
          hidden={!feat.audienceSelector.enabled}
          ref={controlsBarRef}
          onClick={handleClick}>
          {controls}
          {actionButton}
        </Styled.ControlsBar>
      </Styled.Wrapper>
    </Fragment>
  );
}

const styles = css`
  .audience-tooltip-popper {
    pointer-events: none !important;
  }

  .pill-tooltip-popper {
    && {
      translate: -8px;
      margin-top: 8px !important;
      margin-bottom: 8px !important;
    }
  }

  .pill-tooltip-arrow {
    && {
      width: 24px;
      height: 24px;
      transform: rotate(45deg) !important;
      left: 12px !important;
      border-radius: 2px;
      background: currentColor;
    }
  }
`;

export default memo(forwardRef(PromptInput));
